import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Camera, 
  Image as ImageIcon, 
  X, 
  Lightbulb, 
  Send, 
  Loader2,
  MessageSquare,
  CheckCircle2,
  Clock,
  ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ImprovementSuggestions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mySuggestions, setMySuggestions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filterTab, setFilterTab] = useState('active'); // 'active', 'archived', 'all'
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [photos, setPhotos] = useState([]);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    // Open form by default for employees
    setShowForm(true);
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      const [suggestionsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/api/improvement-suggestions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        // Use /api/user/locations endpoint (works for all roles)
        axios.get(`${API}/api/user/locations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setMySuggestions(suggestionsRes.data || []);
      
      // Filter locations based on user's assigned locations
      let availableLocations = locationsRes.data || [];
      if (user && user.location_ids && user.location_ids.length > 0) {
        availableLocations = availableLocations.filter(loc => 
          user.location_ids.includes(loc.id)
        );
      }
      
      setLocations(availableLocations);
      
      // Auto-select first location
      if (availableLocations.length > 0) {
        setSelectedLocation(availableLocations[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = (e, isCamera) => {
    const files = Array.from(e.target.files);
    
    if (photos.length + files.length > 3) {
      toast.error('Maximal 3 Fotos erlaubt');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Datei zu groß: ${file.name} (max 5MB)`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        // Compress image
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if too large
          const maxDimension = 1920;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with quality 0.7
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          setPhotos(prev => [...prev, compressedBase64]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }
    if (!description.trim()) {
      toast.error('Bitte geben Sie eine Beschreibung ein');
      return;
    }
    if (!selectedLocation) {
      toast.error('Bitte wählen Sie einen Standort');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/improvement-suggestions`, {
        title: title.trim(),
        description: description.trim(),
        location_id: selectedLocation,
        photos: photos
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Verbesserungsvorschlag erfolgreich eingereicht!');
      setShowForm(false);
      setTitle('');
      setDescription('');
      setPhotos([]);
      fetchData();
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status, archived) => {
    if (archived) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800">
          <X className="h-3 w-3" />
          Archiviert
        </span>
      );
    }
    
    const badges = {
      pending: { text: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      reviewed: { text: 'Geprüft', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
      forwarded: { text: 'Weitergeleitet', color: 'bg-purple-100 text-purple-800', icon: ArrowRight },
      implemented: { text: 'Umgesetzt', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      rejected: { text: 'Abgelehnt', color: 'bg-red-100 text-red-800', icon: X }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.text}
      </span>
    );
  };

  const filteredSuggestions = mySuggestions.filter(s => {
    if (filterTab === 'active') return !s.archived;
    if (filterTab === 'archived') return s.archived;
    return true; // 'all'
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Lightbulb className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500" />
              Verbesserungsvorschläge
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Teilen Sie Ihre Ideen mit uns</p>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <Card className="border-yellow-200 shadow-lg">
            <CardHeader className="bg-yellow-50">
              <CardTitle className="text-lg">Neuer Verbesserungsvorschlag</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              {/* Location */}
              <div className="space-y-2">
                <Label>Standort *</Label>
                {locations.length === 0 ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">Keine Standorte verfügbar. Bitte kontaktieren Sie Ihren Administrator.</p>
                  </div>
                ) : (
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                  >
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label>Titel *</Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="z.B. Bessere Beleuchtung im Lager"
                  maxLength={100}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Beschreibung *</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Beschreiben Sie Ihren Vorschlag detailliert..."
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Photo Upload */}
              <div className="space-y-3">
                <Label>Fotos (optional, max. 3)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={photos.length >= 3 || locations.length === 0}
                    className="h-20 flex-col gap-1"
                  >
                    <Camera className="h-6 w-6" />
                    <span className="text-xs">Foto aufnehmen</span>
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={photos.length >= 3 || locations.length === 0}
                    className="h-20 flex-col gap-1"
                  >
                    <ImageIcon className="h-6 w-6" />
                    <span className="text-xs">Aus Galerie</span>
                  </Button>
                </div>

                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handlePhotoCapture(e, true)}
                  className="hidden"
                  multiple
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePhotoCapture(e, false)}
                  className="hidden"
                  multiple
                />

                {/* Photo Preview */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setTitle('');
                    setDescription('');
                    setPhotos([]);
                  }}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || locations.length === 0}
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-gray-900 dark:text-gray-100"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Absenden
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Suggestions */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Meine Vorschläge</h2>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex gap-2 border-b overflow-x-auto">
            <button
              onClick={() => setFilterTab('active')}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                filterTab === 'active'
                  ? 'border-b-2 border-yellow-600 text-yellow-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Aktiv ({mySuggestions.filter(s => !s.archived).length})
            </button>
            <button
              onClick={() => setFilterTab('archived')}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                filterTab === 'archived'
                  ? 'border-b-2 border-yellow-600 text-yellow-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Archiviert ({mySuggestions.filter(s => s.archived).length})
            </button>
            <button
              onClick={() => setFilterTab('all')}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                filterTab === 'all'
                  ? 'border-b-2 border-yellow-600 text-yellow-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Alle ({mySuggestions.length})
            </button>
          </div>
          
          {filteredSuggestions.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Noch keine Vorschläge eingereicht</p>
              </CardContent>
            </Card>
          ) : (
            filteredSuggestions.map(suggestion => (
              <Card key={suggestion.id} className={`hover:shadow-md transition-shadow ${
                suggestion.archived ? 'bg-gray-50' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">{suggestion.title}</h3>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(suggestion.status, suggestion.archived)}
                      {/* Edit button - only if not forwarded and not archived */}
                      {!suggestion.forwarded_to_superadmin && !suggestion.archived && suggestion.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTitle(suggestion.title);
                            setDescription(suggestion.description);
                            setPhotos(suggestion.photos || []);
                            setSelectedLocation(suggestion.location_id);
                            setShowForm(true);
                            // Scroll to top
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="text-xs"
                        >
                          Bearbeiten
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{suggestion.description}</p>
                  
                  {/* Archive Info */}
                  {suggestion.archived && suggestion.archived_reason && (
                    <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300">
                      <strong>Archivierungsgrund:</strong> {suggestion.archived_reason}
                    </div>
                  )}
                  
                  {suggestion.photos && suggestion.photos.length > 0 && (
                    <div className="flex gap-2 mb-3">
                      {suggestion.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt={`Foto ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded cursor-pointer"
                          onClick={() => window.open(photo, '_blank')}
                        />
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>📍 {suggestion.location_name}</span>
                    <span>{new Date(suggestion.created_at).toLocaleDateString('de-DE')}</span>
                  </div>

                  {suggestion.replies && suggestion.replies.length > 0 && (
                    <div className="mt-3 pt-3 border-t space-y-2">
                      <div className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                        <MessageSquare className="h-4 w-4" />
                        Antworten ({suggestion.replies.length})
                      </div>
                      {suggestion.replies.map(reply => (
                        <div key={reply.id} className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{reply.user_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{reply.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};